import {
  DASHBOARD_GRAPH_AUCTIONS_MULTIPUB,
  DASHBOARD_GRAPH_BUYERS_MULTIPUB,
  DashboardMultiPubGraphID,
  IDashboardHomeCardMetricKey,
} from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import { IDashboardChartMetric } from '~/src/dash/components/charts/common';
import {
  formatSelectedBidderGroupIntoStringOfIDs,
  formatSelectedByJoiningIDsWithCommas,
  formatSelectedChannelGroupIntoStringOfIDs,
  formatSelectedDestinationsIntoStringOfSiteIDs,
  formatSelectedRouteIntoStringOfSiteIDs,
  formatSelectedToStringWithEscapes,
  formatSelectedToUpperCaseStringWithCommasAndEscapes,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import {
  IListViewTableColumns,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { DateRange } from '~/src/dash/utils/dateTimeRanges';
import {
  clearedActiveFilters,
  IActiveFilters,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';

export interface IDashboardSubTable {
  columns: IListViewTableColumns;
}

export interface IDashboardTopDemandSourcesTable extends IDashboardSubTable {
  activeMetricKeys: string[];
}

export interface IDashboardParentCardsConfig {
  groupings: Record<DashboardMultiPubGraphID, string[]>;
  metrics: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
}

export interface IDashboardParentOverviewConfig {
  defaultState: {
    activeFilters: IActiveFilters;
    dateRange: DateRange;
    graphID: DashboardMultiPubGraphID;
    grouping: number;
    isCompareEnabled: boolean;
    timezone?: string;
    displayedMetricKeys: IDashboardHomeCardMetricKey[];
    activeChartMetrics: IDashboardChartMetric[];
    isPeriodMenuOpen: boolean;
    isFilterMenuOpen: boolean;
    tableRowsPerPage: RowsPerPage;
    allPubIDs: string[];
    isDemandSourceUsingBidderType: boolean;
  };
  orderedFilterKeys: string[];
  filters: ICustomAPIFilterConfig;
  cards: IDashboardParentCardsConfig;
}

// IMPORTANT: PLEASE READ THE README BEFORE EDITING -> https://publica-project.atlassian.net/wiki/x/DQDXpQ
export const dashboardParentOverviewConfig: IDashboardParentOverviewConfig = {
  defaultState: {
    activeFilters: clearedActiveFilters,
    dateRange: 'Last 7 Days',
    graphID: DASHBOARD_GRAPH_AUCTIONS_MULTIPUB,
    grouping: EDateGroup.ByDay,
    isCompareEnabled: true,
    isPeriodMenuOpen: true,
    isFilterMenuOpen: false,
    displayedMetricKeys: [
      'Revenue',
      'Impressions',
      'CPM',
      'EndpointRequests',
      'RenderRate',
      'PodFillRate',
    ],
    activeChartMetrics: [
      {
        key: 'Revenue',
        label: 'Revenue',
        chartColorIndex: 0,
        chartType: 'bar',
        valueType: 'currency',
        yScale: 'base',
      },
    ],
    tableRowsPerPage: 10,
    allPubIDs: [],
    isDemandSourceUsingBidderType: false,
  },
  orderedFilterKeys: [
    'Publishers',
    'Filtered Demand Sources',
    'Bidders',
    'Bidder Groups',
    'Channels',
    'Channel Groups',
    'Routes',
    'Destinations',
    'Device Types',
  ],
  filters: {
    Publishers: {
      queryKey: 'publisher_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      category: 'publisher',
      displayID: true,
    },
    'Filtered Demand Sources': {
      queryKey: 'biddercodes',
      formatRequestValue: formatSelectedToStringWithEscapes,
      category: 'demand',
      isGroup: true,
    },
    Bidders: {
      queryKey: 'bidder_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 100,
      displayID: true,
      category: 'demand',
    },
    'Bidder Groups': {
      queryKey: 'bidder_ids',
      formatRequestValue: formatSelectedBidderGroupIntoStringOfIDs,
      limit: 1,
      displayID: true,
      category: 'demand',
      isGroup: true,
      isSubset: true,
    },
    Channels: {
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 20,
      displayID: true,
      category: 'supply',
    },
    'Channel Groups': {
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedChannelGroupIntoStringOfIDs,
      limit: 1,
      displayID: true,
      category: 'supply',
      isGroup: true,
      isSubset: true,
    },
    Routes: {
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedRouteIntoStringOfSiteIDs,
      limit: 1,
      category: 'supply',
      isSubset: true,
    },
    Destinations: {
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedDestinationsIntoStringOfSiteIDs,
      limit: 20,
      category: 'supply',
      isSubset: true,
    },
    'Device Types': {
      queryKey: 'devicetypes',
      formatRequestValue: formatSelectedToUpperCaseStringWithCommasAndEscapes,
      category: 'device',
    },
  } as ICustomAPIFilterConfig,
  cards: {
    groupings: {
      [DASHBOARD_GRAPH_AUCTIONS_MULTIPUB]: [
        'Revenue',
        'Impressions',
        'CPM',
        'EndpointRequests',
        'RenderRate',
        'PodFillRate',
        'Auctions',
        'CPMPerSecond',
        'EndpointRequestsECPM',
        'FillRate',
        'PodClearRate',
        'PaidPodClearRate',
      ],
      [DASHBOARD_GRAPH_BUYERS_MULTIPUB]: [
        'Revenue',
        'Impressions',
        'CPM',
        'WinningBids',
        'RenderRate',
        'BidRequests',
        'BidResponses',
        'BidResponseRate',
        'BidTimeoutRate',
        'CPMPerSecond',
        'BidWinRate',
      ],
    },
    metrics: {
      Revenue: {
        key: 'Revenue',
        label: 'Revenue',
        responseKey: 'Revenue',
        trendKey: 'RevenueTrend',
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      Impressions: {
        key: 'Impressions',
        label: 'Impressions',
        responseKey: 'Impressions',
        trendKey: 'ImpressionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      CPM: {
        key: 'CPM',
        label: 'CPM',
        responseKey: 'CPM',
        trendKey: 'CPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      CPMPerSecond: {
        key: 'CPMPerSecond',
        label: 'CPM Per Second',
        responseKey: 'CPMPerSecond',
        trendKey: 'CPMPerSecondTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      RenderRate: {
        key: 'RenderRate',
        label: 'Render Rate',
        responseKey: 'RenderRate',
        trendKey: 'RenderRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      EndpointRequests: {
        key: 'EndpointRequests',
        label: 'Endpoint Requests',
        responseKey: 'EndpointRequests',
        trendKey: 'EndpointRequestsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      EndpointRequestsECPM: {
        key: 'EndpointRequestsECPM',
        label: 'Endpoint Requests eCPM',
        responseKey: 'EndpointRequestsECPM',
        trendKey: 'EndpointRequestsECPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      Auctions: {
        key: 'Auctions',
        label: 'Auctions',
        responseKey: 'Auctions',
        trendKey: 'AuctionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      PodFillRate: {
        key: 'PodFillRate',
        label: 'Pod Fill Rate',
        responseKey: 'PodFillRate',
        trendKey: 'PodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      PodClearRate: {
        key: 'PodClearRate',
        label: 'Pod Clear Rate',
        responseKey: 'PodClearRate',
        trendKey: 'PodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      PaidPodClearRate: {
        key: 'PaidPodClearRate',
        label: 'Paid Pod Clear Rate',
        responseKey: 'PaidPodClearRate',
        trendKey: 'PaidPodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      FillRate: {
        key: 'FillRate',
        label: 'Fill Rate',
        responseKey: 'FillRate',
        trendKey: 'FillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidRequests: {
        key: 'BidRequests',
        label: 'Bid Requests',
        responseKey: 'BidRequests',
        trendKey: 'BidRequestsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidResponses: {
        key: 'BidResponses',
        label: 'Bid Responses',
        responseKey: 'BidResponses',
        trendKey: 'BidResponsesTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      WinningBids: {
        key: 'WinningBids',
        label: 'Winning Bids',
        responseKey: 'WinningBids',
        trendKey: 'WinningBidsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidWinRate: {
        key: 'BidWinRate',
        label: 'Bid Win Rate',
        responseKey: 'BidWinRate',
        trendKey: 'BidWinRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidResponseRate: {
        key: 'BidResponseRate',
        label: 'Bid Response Rate',
        responseKey: 'BidResponseRate',
        trendKey: 'BidResponseRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidTimeoutRate: {
        isInverseTrend: true,
        key: 'BidTimeoutRate',
        label: 'Bid Timeout Rate',
        responseKey: 'BidTimeoutRate',
        trendKey: 'BidTimeoutRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
    } as Record<IDashboardHomeCardMetricKey, IDashboardCard>,
  },
};
